import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Stack,
    Alert,
    CircularProgress,
    TextField
} from '@mui/material';
import { employerApi } from '../services/apiClient';
import { toast } from 'react-toastify';

interface CancelApplicationDialogProps {
    open: boolean;
    onClose: () => void;
    application: any | null;
    onSuccess: () => void;
}

const CANCEL_REASONS = [
    'Employee no longer needs this benefit',
    'Employee leaving the business',
    'Company policy change',
    'Other (please specify)'
];

const CancelApplicationDialog = ({ open, onClose, application, onSuccess }: CancelApplicationDialogProps) => {
    const [selectedReason, setSelectedReason] = useState('');
    const [otherReason, setOtherReason] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const requiresOtherReason = selectedReason === 'Other (please specify)';

    const handleSubmit = async () => {
        if (!selectedReason) {
            setError('Please select a cancellation reason');
            return;
        }

        if (requiresOtherReason && !otherReason.trim()) {
            setError('Please provide a reason for cancellation');
            return;
        }

        if (!application) return;

        setIsSubmitting(true);
        setError(null);

        try {
            const reason = requiresOtherReason ? otherReason.trim() : selectedReason;
            await employerApi.post(`/applications/${application.id}/cancel`, {
                reason
            });
            onSuccess();
        } catch (error: any) {
            console.error('Error cancelling application:', error);
            setError(error?.message || 'Failed to cancel application');
            toast.error(error?.message || 'Failed to cancel application');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        setSelectedReason('');
        setOtherReason('');
        setError(null);
        onClose();
    };

    if (!application) return null;

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
            <DialogTitle>Cancel Application</DialogTitle>
            <DialogContent dividers>
                <Stack spacing={3}>
                    <Alert severity="warning">
                        Are you sure you want to cancel this application? This action cannot be undone.
                    </Alert>

                    <Typography variant="body2">
                        Please provide a reason for cancelling this application:
                    </Typography>

                    {error && <Alert severity="error">{error}</Alert>}

                    <Stack spacing={2}>
                        {CANCEL_REASONS.map((reason) => (
                            <button
                                key={reason}
                                onClick={() => {
                                    setSelectedReason(reason);
                                    setError(null);
                                }}
                                disabled={isSubmitting}
                                className={`flex justify-start ${selectedReason === reason ? 'bg-red/30 hover:bg-red/10' : ''}`}
                            >
                                {reason}
                            </button>
                        ))}
                    </Stack>

                    {requiresOtherReason && (
                        <TextField
                            multiline
                            rows={3}
                            label="Please specify the reason"
                            value={otherReason}
                            onChange={(e) => {
                                setOtherReason(e.target.value);
                                setError(null);
                            }}
                            placeholder="Provide details about why this application is being cancelled..."
                            fullWidth
                            disabled={isSubmitting}
                        />
                    )}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={isSubmitting}>
                    Cancel
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    color="error"
                    disabled={!selectedReason || (requiresOtherReason && !otherReason.trim()) || isSubmitting}
                    startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
                >
                    {isSubmitting ? 'Cancelling...' : 'Confirm Cancellation'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CancelApplicationDialog;

